import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';

/**
 * Interface for email notification parameters
 */
interface EmailNotificationParams {
  to: string;
  subject: string;
  templateName: string;
  templateData: any;
}

/**
 * Types of email templates available
 */
export enum EmailTemplate {
  FRIEND_REQUEST = 'FRIEND_REQUEST',
  FRIEND_REQUEST_ACCEPTED = 'FRIEND_REQUEST_ACCEPTED',
  FRIEND_REQUEST_DECLINED = 'FRIEND_REQUEST_DECLINED',
  RECEIPT = 'RECEIPT',
  PASSWORD_RESET = 'PASSWORD_RESET',
  WELCOME = 'WELCOME'
}

/**
 * Send email notification.
 *
 * Email delivery is not yet configured. This function does not send mail and
 * returns false so callers can detect that no notification was delivered.
 */
export const sendEmailNotification = async (_params: EmailNotificationParams): Promise<boolean> => {
  console.warn('[EMAIL SERVICE] Email delivery is not configured; notification not sent.');
  return false;
};

/**
 * Escape HTML special characters to prevent XSS in email templates.
 * Applies to any user-provided string interpolated into HTML content.
 */
const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Generate email content from template
 */
interface EmailContent {
  html: string;
  text: string;
}

const generateEmailContent = (templateName: string, data: any): EmailContent => {
  switch (templateName) {
    case EmailTemplate.FRIEND_REQUEST:
      return generateFriendRequestEmail(data);
    case EmailTemplate.FRIEND_REQUEST_ACCEPTED:
      return generateFriendRequestAcceptedEmail(data);
    case EmailTemplate.FRIEND_REQUEST_DECLINED:
      return generateFriendRequestDeclinedEmail(data);
    case EmailTemplate.RECEIPT:
      return { html: '', text: '' };
    case EmailTemplate.PASSWORD_RESET:
      return { html: '', text: '' };
    case EmailTemplate.WELCOME:
      return { html: '', text: '' };
    default:
      return { html: '', text: '' };
  }
};

/**
 * Generate friend request email content
 */
const generateFriendRequestEmail = (data: {
  requesterId: string;
  requesterName: string;
  requesterPhotoURL?: string;
  requesterDepartment?: string;
  recipientName: string;
  appUrl: string;
  friendsPageUrl: string;
}): EmailContent => {
  const { requesterName, recipientName, requesterDepartment, appUrl, friendsPageUrl } = data;

  // Sanitize user-provided values before interpolating into HTML
  const safeRecipientName = escapeHtml(recipientName);
  const safeRequesterName = escapeHtml(requesterName);
  const safeRequesterDepartment = requesterDepartment ? escapeHtml(requesterDepartment) : '';

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px;">
      <div style="text-align: center; margin-bottom: 20px;">
        <h2 style="color: #1976d2; margin-bottom: 10px;">New Friend Request</h2>
      </div>
      <p>Hello ${safeRecipientName},</p>
      <p><strong>${safeRequesterName}</strong> ${safeRequesterDepartment ? `from ${safeRequesterDepartment}` : ''} has sent you a friend request on Superior Payroll.</p>
      <p>Connecting with colleagues can enhance your collaboration and make payroll management more efficient!</p>
      <div style="text-align: center; margin: 30px 0;">
        <a href="${friendsPageUrl}" style="background-color: #1976d2; color: white; padding: 12px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">View Friend Requests</a>
      </div>
      <p>You can manage all your connections and pending requests on the Friends page.</p>
      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
      <p style="font-size: 12px; color: #757575; text-align: center;">
        This is an automated message from Superior Payroll. Please do not reply to this email.
        <br>
        <a href="${appUrl}" style="color: #1976d2;">Visit Superior Payroll</a>
      </p>
    </div>
  `;
  
  const text = `
Hello ${recipientName},

${requesterName} ${requesterDepartment ? `from ${requesterDepartment}` : ''} has sent you a friend request on Superior Payroll.

Connecting with colleagues can enhance your collaboration and make payroll management more efficient!

To view and manage friend requests, visit: ${friendsPageUrl}

This is an automated message from Superior Payroll. Please do not reply to this email.
  `;
  
  return { html, text };
};

/**
 * Generate friend request accepted email content
 */
const generateFriendRequestAcceptedEmail = (data: {
  acceptorId: string;
  acceptorName: string;
  acceptorPhotoURL?: string;
  acceptorDepartment?: string;
  requesterName: string;
  appUrl: string;
  friendsPageUrl: string;
}): EmailContent => {
  const { acceptorName, requesterName, acceptorDepartment, appUrl, friendsPageUrl } = data;

  // Sanitize user-provided values before interpolating into HTML
  const safeRequesterName = escapeHtml(requesterName);
  const safeAcceptorName = escapeHtml(acceptorName);
  const safeAcceptorDepartment = acceptorDepartment ? escapeHtml(acceptorDepartment) : '';

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px;">
      <div style="text-align: center; margin-bottom: 20px;">
        <h2 style="color: #4caf50; margin-bottom: 10px;">Friend Request Accepted</h2>
      </div>
      <p>Hello ${safeRequesterName},</p>
      <p>Great news! <strong>${safeAcceptorName}</strong> ${safeAcceptorDepartment ? `from ${safeAcceptorDepartment}` : ''} has accepted your friend request on Superior Payroll.</p>
      <p>You can now collaborate more efficiently on payroll tasks and share information securely.</p>
      <div style="text-align: center; margin: 30px 0;">
        <a href="${friendsPageUrl}" style="background-color: #4caf50; color: white; padding: 12px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">View Your Connections</a>
      </div>
      <p>You can message your connections and manage all your network from the Friends page.</p>
      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
      <p style="font-size: 12px; color: #757575; text-align: center;">
        This is an automated message from Superior Payroll. Please do not reply to this email.
        <br>
        <a href="${appUrl}" style="color: #4caf50;">Visit Superior Payroll</a>
      </p>
    </div>
  `;
  
  const text = `
Hello ${requesterName},

Great news! ${acceptorName} ${acceptorDepartment ? `from ${acceptorDepartment}` : ''} has accepted your friend request on Superior Payroll.

You can now collaborate more efficiently on payroll tasks and share information securely.

To view your connections, visit: ${friendsPageUrl}

This is an automated message from Superior Payroll. Please do not reply to this email.
  `;
  
  return { html, text };
};

/**
 * Generate friend request declined email content
 */
const generateFriendRequestDeclinedEmail = (data: {
  declinerId: string;
  declinerName: string;
  requesterName: string;
  appUrl: string;
  friendsPageUrl: string;
}): EmailContent => {
  const { declinerName, requesterName, appUrl, friendsPageUrl } = data;

  // Sanitize user-provided values before interpolating into HTML
  const safeRequesterName = escapeHtml(requesterName);
  const safeDeclinerName = escapeHtml(declinerName);

  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px;">
      <div style="text-align: center; margin-bottom: 20px;">
        <h2 style="color: #757575; margin-bottom: 10px;">Friend Request Update</h2>
      </div>
      <p>Hello ${safeRequesterName},</p>
      <p>${safeDeclinerName} is unable to accept your friend request at this time.</p>
      <p>Don't worry, you can still connect with other colleagues to collaborate on payroll tasks.</p>
      <div style="text-align: center; margin: 30px 0;">
        <a href="${friendsPageUrl}" style="background-color: #757575; color: white; padding: 12px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">Find More Colleagues</a>
      </div>
      <p>You can discover and connect with more team members from the Friends page.</p>
      <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
      <p style="font-size: 12px; color: #757575; text-align: center;">
        This is an automated message from Superior Payroll. Please do not reply to this email.
        <br>
        <a href="${appUrl}" style="color: #757575;">Visit Superior Payroll</a>
      </p>
    </div>
  `;
  
  const text = `
Hello ${requesterName},

${declinerName} is unable to accept your friend request at this time.

Don't worry, you can still connect with other colleagues to collaborate on payroll tasks.

To find more colleagues, visit: ${friendsPageUrl}

This is an automated message from Superior Payroll. Please do not reply to this email.
  `;
  
  return { html, text };
}; 